import { useState, useEffect, useCallback, useMemo } from "react";

/* ═══════════════════════════════════════════════════════════════════
   Data Model
   ═══════════════════════════════════════════════════════════════════ */

interface TaskHistoryEntry {
  id: string;
  timestamp: string;
  duration: number; // ms
  status: "success" | "failure";
  output: string;
}

interface ScheduledTask {
  id: string;
  name: string;
  schedule: string; // human-readable
  cronExpr: string;
  actionType: ActionType;
  script: string;
  enabled: boolean;
  lastRun: string | null;
  nextRun: string;
  history: TaskHistoryEntry[];
}

type ActionType =
  | "run_command"
  | "send_report"
  | "check_drift"
  | "cleanup_files"
  | "backup_data"
  | "custom_script";

const ACTION_TYPE_LABELS: Record<ActionType, string> = {
  run_command: "Run Command",
  send_report: "Send Report",
  check_drift: "Check Drift",
  cleanup_files: "Cleanup Files",
  backup_data: "Backup Data",
  custom_script: "Custom Script",
};

const STORAGE_KEY = "opentool_scheduled_tasks";

/* ── Schedule presets ── */
interface SchedulePreset {
  label: string;
  schedule: string;
  cronExpr: string;
}

const SCHEDULE_PRESETS: SchedulePreset[] = [
  { label: "Every 15 minutes", schedule: "Every 15 minutes", cronExpr: "*/15 * * * *" },
  { label: "Hourly", schedule: "Every hour", cronExpr: "0 * * * *" },
  { label: "Daily at midnight", schedule: "Daily at midnight", cronExpr: "0 0 * * *" },
  { label: "Daily at 9am", schedule: "Every day at 9:00 AM", cronExpr: "0 9 * * *" },
  { label: "Weekly on Monday", schedule: "Weekly on Monday at 9:00 AM", cronExpr: "0 9 * * 1" },
  { label: "Monthly (1st)", schedule: "Monthly on the 1st at midnight", cronExpr: "0 0 1 * *" },
  { label: "Custom", schedule: "", cronExpr: "" },
];

/* ── Templates ── */
interface TaskTemplate {
  name: string;
  description: string;
  schedule: string;
  cronExpr: string;
  actionType: ActionType;
  script: string;
  icon: string;
  color: string;
}

const TEMPLATES: TaskTemplate[] = [
  {
    name: "Daily Cleanup",
    description: "Remove temp files, clear caches",
    schedule: "Daily at 2:00 AM",
    cronExpr: "0 2 * * *",
    actionType: "cleanup_files",
    script: "# Remove temporary files older than 7 days\nfind /tmp -type f -mtime +7 -delete\n\n# Clear npm cache\nnpm cache clean --force\n\n# Clear Docker build cache\ndocker system prune -f",
    icon: "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
    color: "#f59e0b",
  },
  {
    name: "Weekly Drift Check",
    description: "Compare deployed config vs source",
    schedule: "Weekly on Monday at 9:00 AM",
    cronExpr: "0 9 * * 1",
    actionType: "check_drift",
    script: "# Compare live Helm values against committed values\nhelm get values opentool -n opentool > /tmp/live-values.yaml\ndiff /tmp/live-values.yaml deploy/helm/apps/opentool/values.yaml\n\n# Check Terraform drift\ncd infra/terraform/stacks/opentool-west-dev\nterraform plan -detailed-exitcode",
    icon: "M22 12h-4l-3 9L9 3l-3 9H2",
    color: "#8b5cf6",
  },
  {
    name: "Documentation Sync",
    description: "Regenerate docs from source",
    schedule: "Daily at 6:00 AM",
    cronExpr: "0 6 * * *",
    actionType: "run_command",
    script: "# Generate API docs from protobuf\nprotoc --doc_out=./site/docs --doc_opt=html,api.html proto/*.proto\n\n# Rebuild architecture diagrams\nnpx ts-node scripts/gen-arch-diagram.ts\n\n# Push to GitHub Pages\ngit -C site add . && git -C site commit -m 'docs: auto-sync' && git -C site push",
    icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    color: "#06b6d4",
  },
  {
    name: "Cost Report",
    description: "Generate weekly spend summary",
    schedule: "Weekly on Friday at 5:00 PM",
    cronExpr: "0 17 * * 5",
    actionType: "send_report",
    script: "# Pull AWS cost data for the week\naws ce get-cost-and-usage \\\n  --time-period Start=$(date -d '7 days ago' +%Y-%m-%d),End=$(date +%Y-%m-%d) \\\n  --granularity DAILY \\\n  --metrics BlendedCost \\\n  --group-by Type=DIMENSION,Key=SERVICE\n\n# Generate summary report\necho 'Weekly cost report generated'",
    icon: "M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6",
    color: "#10b981",
  },
  {
    name: "Health Monitor",
    description: "Check all endpoints every 15 min",
    schedule: "Every 15 minutes",
    cronExpr: "*/15 * * * *",
    actionType: "run_command",
    script: "#!/bin/bash\nENDPOINTS=(\n  \"https://app.dev.opentool.io/api/health\"\n  \"https://app.opentool.io/api/health\"\n  \"https://pages.opentool.io\"\n)\n\nfor url in \"${ENDPOINTS[@]}\"; do\n  status=$(curl -s -o /dev/null -w '%{http_code}' \"$url\")\n  if [ \"$status\" -ne 200 ]; then\n    echo \"ALERT: $url returned $status\"\n  else\n    echo \"OK: $url ($status)\"\n  fi\ndone",
    icon: "M22 12h-4l-3 9L9 3l-3 9H2",
    color: "#ef4444",
  },
  {
    name: "Backup Workspace",
    description: "Snapshot workspace data",
    schedule: "Daily at midnight",
    cronExpr: "0 0 * * *",
    actionType: "backup_data",
    script: "# Create timestamped backup\nBACKUP_DIR=\"/backups/workspace/$(date +%Y%m%d_%H%M%S)\"\nmkdir -p \"$BACKUP_DIR\"\n\n# Export DynamoDB table\naws dynamodb scan --table-name opentool-dev > \"$BACKUP_DIR/dynamodb.json\"\n\n# Backup local workspace files\ntar czf \"$BACKUP_DIR/workspace.tar.gz\" ~/workspace/\n\n# Prune backups older than 30 days\nfind /backups/workspace -maxdepth 1 -mtime +30 -exec rm -rf {} +",
    icon: "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4",
    color: "#3b82f6",
  },
];

/* ── Helpers ── */

function generateId(): string {
  return `task_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function computeNextRun(cronExpr: string): string {
  // Simple next-run approximation based on cron expression
  const now = new Date();
  const parts = cronExpr.split(" ");
  if (parts.length !== 5) return new Date(now.getTime() + 3600000).toISOString();

  const [minute, hour, dayOfMonth, , dayOfWeek] = parts;

  const next = new Date(now);

  if (minute.startsWith("*/")) {
    const interval = parseInt(minute.slice(2));
    const nextMin = Math.ceil((now.getMinutes() + 1) / interval) * interval;
    if (nextMin >= 60) {
      next.setHours(next.getHours() + 1);
      next.setMinutes(nextMin - 60);
    } else {
      next.setMinutes(nextMin);
    }
    next.setSeconds(0);
    return next.toISOString();
  }

  const targetMinute = minute === "*" ? 0 : parseInt(minute);
  const targetHour = hour === "*" ? now.getHours() : parseInt(hour);

  next.setMinutes(targetMinute);
  next.setSeconds(0);
  next.setMilliseconds(0);

  if (hour !== "*") {
    next.setHours(targetHour);
  }

  if (dayOfWeek !== "*") {
    const targetDay = parseInt(dayOfWeek);
    const currentDay = now.getDay();
    let daysUntil = targetDay - currentDay;
    if (daysUntil < 0 || (daysUntil === 0 && next <= now)) daysUntil += 7;
    next.setDate(next.getDate() + daysUntil);
  } else if (dayOfMonth !== "*") {
    const targetDate = parseInt(dayOfMonth);
    next.setDate(targetDate);
    if (next <= now) next.setMonth(next.getMonth() + 1);
  } else if (next <= now) {
    if (hour !== "*") {
      next.setDate(next.getDate() + 1);
    } else {
      next.setHours(next.getHours() + 1);
    }
  }

  return next.toISOString();
}

function formatDate(iso: string | null): string {
  if (!iso) return "Never";
  const d = new Date(iso);
  const now = new Date();
  const diff = now.getTime() - d.getTime();

  if (diff < 0) {
    // Future date
    const absDiff = Math.abs(diff);
    if (absDiff < 60000) return "in < 1m";
    if (absDiff < 3600000) return `in ${Math.floor(absDiff / 60000)}m`;
    if (absDiff < 86400000) return `in ${Math.floor(absDiff / 3600000)}h`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  }

  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

/* ═══════════════════════════════════════════════════════════════════
   Styles (inline — no external deps)
   ═══════════════════════════════════════════════════════════════════ */

const S = {
  root: {
    display: "flex",
    flexDirection: "column" as const,
    height: "100%",
    background: "rgba(8, 8, 18, 0.95)",
    color: "#e6edf3",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderBottom: "1px solid rgba(79, 143, 255, 0.1)",
    flexShrink: 0,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  headerTitle: {
    fontSize: "18px",
    fontWeight: 700,
    letterSpacing: "-0.02em",
    color: "#e6edf3",
  },
  headerSubtitle: {
    fontSize: "12px",
    color: "rgba(230, 237, 243, 0.45)",
    fontFamily: "'JetBrains Mono', monospace",
  },
  addButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 16px",
    background: "rgba(79, 143, 255, 0.15)",
    border: "1px solid rgba(79, 143, 255, 0.3)",
    borderRadius: "8px",
    color: "#4f8fff",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  body: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  /* ── Left panel: task list ── */
  listPanel: {
    width: "340px",
    borderRight: "1px solid rgba(79, 143, 255, 0.08)",
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
    flexShrink: 0,
  },
  listHeader: {
    padding: "12px 16px",
    fontSize: "11px",
    fontWeight: 600,
    color: "rgba(230, 237, 243, 0.4)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    borderBottom: "1px solid rgba(79, 143, 255, 0.06)",
  },
  listScroll: {
    flex: 1,
    overflowY: "auto" as const,
    padding: "8px",
  },
  taskCard: (selected: boolean) => ({
    padding: "12px 14px",
    borderRadius: "8px",
    cursor: "pointer",
    marginBottom: "4px",
    background: selected ? "rgba(79, 143, 255, 0.1)" : "transparent",
    border: selected ? "1px solid rgba(79, 143, 255, 0.25)" : "1px solid transparent",
    transition: "all 0.15s ease",
  }),
  taskName: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#e6edf3",
    marginBottom: "4px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  statusDot: (enabled: boolean) => ({
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: enabled ? "#22c55e" : "#6b7280",
    flexShrink: 0,
    boxShadow: enabled ? "0 0 6px rgba(34, 197, 94, 0.4)" : "none",
  }),
  taskMeta: {
    fontSize: "12px",
    color: "rgba(230, 237, 243, 0.45)",
    fontFamily: "'JetBrains Mono', monospace",
    display: "flex",
    flexDirection: "column" as const,
    gap: "2px",
  },
  /* ── Right panel: editor ── */
  editorPanel: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
  },
  editorScroll: {
    flex: 1,
    overflowY: "auto" as const,
    padding: "24px",
  },
  fieldGroup: {
    marginBottom: "20px",
  },
  fieldLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: "rgba(230, 237, 243, 0.5)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    marginBottom: "6px",
  },
  textInput: {
    width: "100%",
    padding: "10px 14px",
    background: "rgba(255, 255, 255, 0.04)",
    border: "1px solid rgba(79, 143, 255, 0.15)",
    borderRadius: "8px",
    color: "#e6edf3",
    fontSize: "14px",
    fontFamily: "'Inter', sans-serif",
    outline: "none",
    boxSizing: "border-box" as const,
    transition: "border-color 0.15s ease",
  },
  select: {
    width: "100%",
    padding: "10px 14px",
    background: "rgba(255, 255, 255, 0.04)",
    border: "1px solid rgba(79, 143, 255, 0.15)",
    borderRadius: "8px",
    color: "#e6edf3",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box" as const,
    cursor: "pointer",
    appearance: "none" as const,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    paddingRight: "36px",
  },
  textarea: {
    width: "100%",
    minHeight: "180px",
    padding: "12px 14px",
    background: "rgba(255, 255, 255, 0.04)",
    border: "1px solid rgba(79, 143, 255, 0.15)",
    borderRadius: "8px",
    color: "#e6edf3",
    fontSize: "13px",
    fontFamily: "'JetBrains Mono', monospace",
    lineHeight: "1.6",
    outline: "none",
    boxSizing: "border-box" as const,
    resize: "vertical" as const,
    transition: "border-color 0.15s ease",
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 0",
  },
  toggle: (on: boolean) => ({
    width: "42px",
    height: "24px",
    borderRadius: "12px",
    background: on ? "#4f8fff" : "rgba(255, 255, 255, 0.1)",
    border: "none",
    cursor: "pointer",
    position: "relative" as const,
    transition: "background 0.2s ease",
    flexShrink: 0,
  }),
  toggleKnob: (on: boolean) => ({
    position: "absolute" as const,
    top: "3px",
    left: on ? "21px" : "3px",
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    background: "#fff",
    transition: "left 0.2s ease",
  }),
  buttonRow: {
    display: "flex",
    gap: "10px",
    padding: "16px 24px",
    borderTop: "1px solid rgba(79, 143, 255, 0.08)",
    flexShrink: 0,
  },
  btnPrimary: {
    padding: "10px 24px",
    background: "rgba(79, 143, 255, 0.2)",
    border: "1px solid rgba(79, 143, 255, 0.4)",
    borderRadius: "8px",
    color: "#4f8fff",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  btnDanger: {
    padding: "10px 24px",
    background: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.3)",
    borderRadius: "8px",
    color: "#ef4444",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
    marginLeft: "auto",
  },
  btnRun: {
    padding: "10px 24px",
    background: "rgba(34, 197, 94, 0.15)",
    border: "1px solid rgba(34, 197, 94, 0.3)",
    borderRadius: "8px",
    color: "#22c55e",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  /* ── Templates ── */
  templatesContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "40px",
    overflowY: "auto" as const,
  },
  templatesTitle: {
    fontSize: "20px",
    fontWeight: 700,
    color: "#e6edf3",
    marginBottom: "4px",
    textAlign: "center" as const,
  },
  templatesSubtitle: {
    fontSize: "13px",
    color: "rgba(230, 237, 243, 0.45)",
    marginBottom: "28px",
    textAlign: "center" as const,
  },
  templatesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "14px",
    width: "100%",
    maxWidth: "860px",
  },
  templateCard: (color: string) => ({
    padding: "18px",
    background: "rgba(255, 255, 255, 0.03)",
    border: `1px solid ${color}22`,
    borderRadius: "12px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  }),
  templateIcon: (color: string) => ({
    width: "36px",
    height: "36px",
    borderRadius: "10px",
    background: `${color}18`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "10px",
  }),
  templateName: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#e6edf3",
    marginBottom: "4px",
  },
  templateDesc: {
    fontSize: "12px",
    color: "rgba(230, 237, 243, 0.45)",
    marginBottom: "8px",
  },
  templateSchedule: {
    fontSize: "11px",
    color: "rgba(230, 237, 243, 0.3)",
    fontFamily: "'JetBrains Mono', monospace",
  },
  /* ── History ── */
  historyPanel: {
    borderTop: "1px solid rgba(79, 143, 255, 0.08)",
    maxHeight: "200px",
    overflowY: "auto" as const,
    flexShrink: 0,
  },
  historyHeader: {
    padding: "10px 24px",
    fontSize: "11px",
    fontWeight: 600,
    color: "rgba(230, 237, 243, 0.4)",
    textTransform: "uppercase" as const,
    letterSpacing: "0.06em",
    borderBottom: "1px solid rgba(79, 143, 255, 0.06)",
    position: "sticky" as const,
    top: 0,
    background: "rgba(8, 8, 18, 0.98)",
  },
  historyRow: {
    display: "grid",
    gridTemplateColumns: "120px 70px 60px 1fr",
    gap: "12px",
    padding: "8px 24px",
    fontSize: "12px",
    fontFamily: "'JetBrains Mono', monospace",
    borderBottom: "1px solid rgba(255, 255, 255, 0.03)",
    alignItems: "center",
  },
  statusBadge: (status: "success" | "failure") => ({
    display: "inline-flex",
    padding: "2px 8px",
    borderRadius: "4px",
    fontSize: "11px",
    fontWeight: 600,
    background: status === "success" ? "rgba(34, 197, 94, 0.12)" : "rgba(239, 68, 68, 0.12)",
    color: status === "success" ? "#22c55e" : "#ef4444",
  }),
  /* ── Empty state ── */
  emptyState: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 20px",
    color: "rgba(230, 237, 243, 0.3)",
    fontSize: "13px",
    textAlign: "center" as const,
  },
  /* ── Tab bar ── */
  tabBar: {
    display: "flex",
    borderBottom: "1px solid rgba(79, 143, 255, 0.08)",
    flexShrink: 0,
  },
  tab: (active: boolean) => ({
    padding: "10px 20px",
    fontSize: "12px",
    fontWeight: 600,
    color: active ? "#4f8fff" : "rgba(230, 237, 243, 0.45)",
    borderBottom: active ? "2px solid #4f8fff" : "2px solid transparent",
    cursor: "pointer",
    background: "transparent",
    border: "none",
    borderBottomWidth: "2px",
    borderBottomStyle: "solid" as const,
    borderBottomColor: active ? "#4f8fff" : "transparent",
    transition: "all 0.15s ease",
  }),
  cronInput: {
    width: "100%",
    padding: "10px 14px",
    background: "rgba(255, 255, 255, 0.04)",
    border: "1px solid rgba(79, 143, 255, 0.15)",
    borderRadius: "8px",
    color: "#e6edf3",
    fontSize: "13px",
    fontFamily: "'JetBrains Mono', monospace",
    outline: "none",
    boxSizing: "border-box" as const,
    marginTop: "6px",
  },
  scheduleHint: {
    fontSize: "11px",
    color: "rgba(230, 237, 243, 0.3)",
    fontFamily: "'JetBrains Mono', monospace",
    marginTop: "4px",
  },
} as const;

/* ═══════════════════════════════════════════════════════════════════
   SVG Icon helper
   ═══════════════════════════════════════════════════════════════════ */

function SvgIcon({ d, size = 16, color = "currentColor" }: { d: string; size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════════════ */

export function SchedulerWorkbench() {
  const [tasks, setTasks] = useState<ScheduledTask[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editorTab, setEditorTab] = useState<"editor" | "history">("editor");

  /* ── Load from localStorage ── */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as ScheduledTask[];
        setTasks(parsed);
      }
    } catch {
      // ignore
    }
  }, []);

  /* ── Persist ── */
  const persist = useCallback((next: ScheduledTask[]) => {
    setTasks(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, []);

  const selectedTask = useMemo(() => tasks.find((t) => t.id === selectedId) ?? null, [tasks, selectedId]);

  /* ── CRUD ── */
  const createTask = useCallback(
    (template?: TaskTemplate) => {
      const id = generateId();
      const newTask: ScheduledTask = {
        id,
        name: template?.name ?? "New Task",
        schedule: template?.schedule ?? "Daily at midnight",
        cronExpr: template?.cronExpr ?? "0 0 * * *",
        actionType: template?.actionType ?? "run_command",
        script: template?.script ?? "# Enter your command here\n",
        enabled: true,
        lastRun: null,
        nextRun: computeNextRun(template?.cronExpr ?? "0 0 * * *"),
        history: [],
      };
      const next = [newTask, ...tasks];
      persist(next);
      setSelectedId(id);
      setEditorTab("editor");
    },
    [tasks, persist],
  );

  const updateTask = useCallback(
    (id: string, patch: Partial<ScheduledTask>) => {
      const next = tasks.map((t) => (t.id === id ? { ...t, ...patch } : t));
      persist(next);
    },
    [tasks, persist],
  );

  const deleteTask = useCallback(
    (id: string) => {
      const next = tasks.filter((t) => t.id !== id);
      persist(next);
      setSelectedId(null);
    },
    [tasks, persist],
  );

  const runTask = useCallback(
    (id: string) => {
      const task = tasks.find((t) => t.id === id);
      if (!task) return;

      const duration = Math.floor(Math.random() * 4000) + 500;
      const success = Math.random() > 0.15; // 85% success rate for simulation
      const entry: TaskHistoryEntry = {
        id: `run_${Date.now()}`,
        timestamp: new Date().toISOString(),
        duration,
        status: success ? "success" : "failure",
        output: success
          ? `Task "${task.name}" completed successfully.\n\n$ ${task.script.split("\n")[0]?.replace(/^#\s*/, "") || "execute"}\nDone in ${formatDuration(duration)}.`
          : `Task "${task.name}" failed.\n\nError: Process exited with code 1\nCheck logs for details.`,
      };

      const updatedHistory = [entry, ...(task.history || [])].slice(0, 10);
      updateTask(id, {
        lastRun: entry.timestamp,
        nextRun: computeNextRun(task.cronExpr),
        history: updatedHistory,
      });
      setEditorTab("history");
    },
    [tasks, updateTask],
  );

  /* ── Render: Templates (empty state) ── */
  const renderTemplates = () => (
    <div style={S.templatesContainer}>
      <div style={S.templatesTitle}>Schedule Task Studio</div>
      <div style={S.templatesSubtitle}>Set up recurring tasks that run automatically. Start from a template or create from scratch.</div>
      <div style={S.templatesGrid}>
        {TEMPLATES.map((tpl) => (
          <div
            key={tpl.name}
            style={S.templateCard(tpl.color)}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLDivElement).style.background = "rgba(255, 255, 255, 0.06)";
              (e.currentTarget as HTMLDivElement).style.borderColor = `${tpl.color}44`;
              (e.currentTarget as HTMLDivElement).style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLDivElement).style.background = "rgba(255, 255, 255, 0.03)";
              (e.currentTarget as HTMLDivElement).style.borderColor = `${tpl.color}22`;
              (e.currentTarget as HTMLDivElement).style.transform = "translateY(0)";
            }}
            onClick={() => createTask(tpl)}
          >
            <div style={S.templateIcon(tpl.color)}>
              <SvgIcon d={tpl.icon} size={18} color={tpl.color} />
            </div>
            <div style={S.templateName}>{tpl.name}</div>
            <div style={S.templateDesc}>{tpl.description}</div>
            <div style={S.templateSchedule}>{tpl.schedule}</div>
          </div>
        ))}
      </div>
    </div>
  );

  /* ── Render: Editor ── */
  const renderEditor = () => {
    if (!selectedTask) {
      return (
        <div style={S.emptyState}>
          <SvgIcon d="M12 2a10 10 0 100 20 10 10 0 000-20z M12 6v6l4 2" size={40} color="rgba(230, 237, 243, 0.15)" />
          <div style={{ marginTop: "12px" }}>Select a task to configure</div>
        </div>
      );
    }

    const isCustomSchedule = !SCHEDULE_PRESETS.some((p) => p.cronExpr === selectedTask.cronExpr && p.label !== "Custom");
    const currentPreset = isCustomSchedule ? "Custom" : SCHEDULE_PRESETS.find((p) => p.cronExpr === selectedTask.cronExpr)?.label ?? "Custom";

    return (
      <div style={S.editorPanel}>
        {/* Tab bar */}
        <div style={S.tabBar}>
          <button style={S.tab(editorTab === "editor")} onClick={() => setEditorTab("editor")}>
            Editor
          </button>
          <button style={S.tab(editorTab === "history")} onClick={() => setEditorTab("history")}>
            Run History {selectedTask.history.length > 0 && `(${selectedTask.history.length})`}
          </button>
        </div>

        {editorTab === "editor" ? (
          <>
            <div style={S.editorScroll}>
              {/* Name */}
              <div style={S.fieldGroup}>
                <div style={S.fieldLabel}>Task Name</div>
                <input
                  type="text"
                  style={S.textInput}
                  value={selectedTask.name}
                  onChange={(e) => updateTask(selectedTask.id, { name: e.target.value })}
                  onFocus={(e) => ((e.target as HTMLInputElement).style.borderColor = "rgba(79, 143, 255, 0.4)")}
                  onBlur={(e) => ((e.target as HTMLInputElement).style.borderColor = "rgba(79, 143, 255, 0.15)")}
                  placeholder="Task name..."
                />
              </div>

              {/* Schedule */}
              <div style={S.fieldGroup}>
                <div style={S.fieldLabel}>Schedule</div>
                <select
                  style={S.select}
                  value={currentPreset}
                  onChange={(e) => {
                    const preset = SCHEDULE_PRESETS.find((p) => p.label === e.target.value);
                    if (preset && preset.label !== "Custom") {
                      updateTask(selectedTask.id, {
                        schedule: preset.schedule,
                        cronExpr: preset.cronExpr,
                        nextRun: computeNextRun(preset.cronExpr),
                      });
                    }
                  }}
                >
                  {SCHEDULE_PRESETS.map((p) => (
                    <option key={p.label} value={p.label}>
                      {p.label}
                    </option>
                  ))}
                </select>
                {(currentPreset === "Custom" || isCustomSchedule) && (
                  <>
                    <input
                      type="text"
                      style={S.cronInput}
                      value={selectedTask.cronExpr}
                      onChange={(e) =>
                        updateTask(selectedTask.id, {
                          cronExpr: e.target.value,
                          schedule: `Custom (${e.target.value})`,
                          nextRun: computeNextRun(e.target.value),
                        })
                      }
                      placeholder="*/5 * * * *"
                    />
                    <div style={S.scheduleHint}>min hour day month weekday</div>
                  </>
                )}
                <div style={S.scheduleHint}>Next run: {formatDate(selectedTask.nextRun)}</div>
              </div>

              {/* Action type */}
              <div style={S.fieldGroup}>
                <div style={S.fieldLabel}>Action Type</div>
                <select
                  style={S.select}
                  value={selectedTask.actionType}
                  onChange={(e) => updateTask(selectedTask.id, { actionType: e.target.value as ActionType })}
                >
                  {Object.entries(ACTION_TYPE_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Script */}
              <div style={S.fieldGroup}>
                <div style={S.fieldLabel}>Script / Command</div>
                <textarea
                  style={S.textarea}
                  value={selectedTask.script}
                  onChange={(e) => updateTask(selectedTask.id, { script: e.target.value })}
                  onFocus={(e) => ((e.target as HTMLTextAreaElement).style.borderColor = "rgba(79, 143, 255, 0.4)")}
                  onBlur={(e) => ((e.target as HTMLTextAreaElement).style.borderColor = "rgba(79, 143, 255, 0.15)")}
                  placeholder="Enter command or script..."
                  spellCheck={false}
                />
              </div>

              {/* Toggle */}
              <div style={S.fieldGroup}>
                <div style={S.toggleRow}>
                  <div>
                    <div style={{ fontSize: "14px", fontWeight: 600, color: "#e6edf3" }}>
                      {selectedTask.enabled ? "Active" : "Paused"}
                    </div>
                    <div style={{ fontSize: "12px", color: "rgba(230, 237, 243, 0.4)" }}>
                      {selectedTask.enabled ? "Task will run on schedule" : "Task is paused and will not execute"}
                    </div>
                  </div>
                  <button
                    style={S.toggle(selectedTask.enabled)}
                    onClick={() => updateTask(selectedTask.id, { enabled: !selectedTask.enabled })}
                  >
                    <div style={S.toggleKnob(selectedTask.enabled)} />
                  </button>
                </div>
              </div>
            </div>

            {/* Button row */}
            <div style={S.buttonRow}>
              <button
                style={S.btnPrimary}
                onClick={() => {
                  /* Already saved via updateTask — show brief confirmation */
                  const btn = document.activeElement as HTMLButtonElement;
                  if (btn) {
                    btn.textContent = "Saved!";
                    setTimeout(() => (btn.textContent = "Save"), 1200);
                  }
                }}
              >
                Save
              </button>
              <button style={S.btnRun} onClick={() => runTask(selectedTask.id)}>
                Run Now
              </button>
              <button style={S.btnDanger} onClick={() => deleteTask(selectedTask.id)}>
                Delete
              </button>
            </div>
          </>
        ) : (
          /* History tab */
          renderHistory()
        )}
      </div>
    );
  };

  /* ── Render: History ── */
  const renderHistory = () => {
    if (!selectedTask) return null;
    const history = selectedTask.history || [];

    if (history.length === 0) {
      return (
        <div style={S.emptyState}>
          <div style={{ marginTop: "40px" }}>No executions yet. Click "Run Now" to test this task.</div>
        </div>
      );
    }

    return (
      <div style={{ flex: 1, overflowY: "auto" as const }}>
        <div style={S.historyHeader}>
          <div style={{ display: "grid", gridTemplateColumns: "120px 70px 60px 1fr", gap: "12px" }}>
            <span>Timestamp</span>
            <span>Duration</span>
            <span>Status</span>
            <span>Output</span>
          </div>
        </div>
        {history.map((entry) => (
          <div key={entry.id} style={S.historyRow}>
            <span style={{ color: "rgba(230, 237, 243, 0.6)" }}>{formatDate(entry.timestamp)}</span>
            <span style={{ color: "rgba(230, 237, 243, 0.5)" }}>{formatDuration(entry.duration)}</span>
            <span style={S.statusBadge(entry.status)}>{entry.status}</span>
            <span
              style={{
                color: "rgba(230, 237, 243, 0.4)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={entry.output}
            >
              {entry.output.split("\n")[0]}
            </span>
          </div>
        ))}
      </div>
    );
  };

  /* ── Render: Task list ── */
  const renderTaskList = () => (
    <div style={S.listPanel}>
      <div style={S.listHeader}>Scheduled Tasks ({tasks.length})</div>
      <div style={S.listScroll}>
        {tasks.length === 0 ? (
          <div style={S.emptyState}>
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--ot-accent, #4f8fff)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 32, height: 32, opacity: 0.5, marginBottom: 4 }}>
              <path d="M12 2a10 10 0 100 20 10 10 0 000-20z M12 6v6l4 2" />
            </svg>
            <div>No scheduled tasks</div>
            <div style={{ fontSize: "11px", marginTop: "4px", color: "var(--ot-text-muted, #6272a4)" }}>Create a task from a template or define a custom schedule. Tasks run on cron intervals and execute agent prompts automatically.</div>
            <div style={{ fontSize: "10px", marginTop: "8px", color: "var(--ot-text-muted, #484f58)" }}>
              <kbd style={{ background: "var(--ot-bg-surface, #1a1a2e)", border: "1px solid var(--ot-border, rgba(255,255,255,0.06))", borderRadius: 3, padding: "0 4px", fontSize: 10 }}>Ctrl+N</kbd> New Task
            </div>
          </div>
        ) : (
          tasks.map((task) => (
            <div
              key={task.id}
              style={S.taskCard(task.id === selectedId)}
              onClick={() => {
                setSelectedId(task.id);
                setEditorTab("editor");
              }}
              onMouseEnter={(e) => {
                if (task.id !== selectedId) {
                  (e.currentTarget as HTMLDivElement).style.background = "rgba(255, 255, 255, 0.03)";
                }
              }}
              onMouseLeave={(e) => {
                if (task.id !== selectedId) {
                  (e.currentTarget as HTMLDivElement).style.background = "transparent";
                }
              }}
            >
              <div style={S.taskName}>
                <div style={S.statusDot(task.enabled)} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{task.name}</span>
              </div>
              <div style={S.taskMeta}>
                <span>{task.schedule}</span>
                <span>Last: {formatDate(task.lastRun)} | Next: {formatDate(task.nextRun)}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );

  /* ── Main render ── */
  const hasTasks = tasks.length > 0;

  return (
    <div style={S.root}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <SvgIcon d="M12 2a10 10 0 100 20 10 10 0 000-20z M12 6v6l4 2" size={22} color="#4f8fff" />
          <div>
            <div style={S.headerTitle}>Scheduler</div>
            <div style={S.headerSubtitle}>Recurring task management</div>
          </div>
        </div>
        <button
          style={S.addButton}
          onClick={() => createTask()}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "rgba(79, 143, 255, 0.25)";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = "rgba(79, 143, 255, 0.15)";
          }}
        >
          <span style={{ fontSize: "16px", lineHeight: 1 }}>+</span> New Task
        </button>
      </div>

      {/* Body */}
      <div style={S.body}>
        {hasTasks ? (
          <>
            {renderTaskList()}
            {renderEditor()}
          </>
        ) : (
          renderTemplates()
        )}
      </div>
    </div>
  );
}
